import React, { useState } from 'react';
import { Search, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import TopicDetailView from './TopicDetailView';

interface HomeTabProps {
  userName: string;
  hideTabsWhenDetail?: (hide: boolean) => void;
  onStartNewConversation?: () => void;
  onStartConversationWithMessage?: (message: string) => void;
}

const HomeTab: React.FC<HomeTabProps> = ({ 
  userName, 
  hideTabsWhenDetail,
  onStartNewConversation,
  onStartConversationWithMessage
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTopic, setSelectedTopic] = useState<any>(null);

  // Get help topics from translations
  const helpTopics = [
    {
      id: "1",
      title: t('chatbot.topics.listService.title'),
      icon: "📝",
      content: t('chatbot.topics.listService.content'),
      startingMessage: t('chatbot.topics.listService.startingMessage'),
      highlighted: false
    },
    {
      id: "2",
      title: t('chatbot.topics.manageBookings.title'),
      icon: "📅",
      content: t('chatbot.topics.manageBookings.content'),
      startingMessage: t('chatbot.topics.manageBookings.startingMessage'),
      highlighted: false
    },
    {
      id: "3",
      title: t('chatbot.topics.paymentOptions.title'),
      icon: "💰",
      content: t('chatbot.topics.paymentOptions.content'),
      startingMessage: t('chatbot.topics.paymentOptions.startingMessage'),
      highlighted: false
    },
    {
      id: "4",
      title: t('chatbot.topics.accountSettings.title'),
      icon: "⚙️",
      content: t('chatbot.topics.accountSettings.content'),
      startingMessage: t('chatbot.topics.accountSettings.startingMessage'),
      highlighted: false
    }
  ];

  const [filteredTopics, setFilteredTopics] = useState(helpTopics);

  // Update filtered topics when language changes
  React.useEffect(() => {
    setFilteredTopics(helpTopics);
  }, [t]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const filtered = helpTopics.filter((topic) =>
      topic.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredTopics(filtered);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    const filtered = helpTopics.filter((topic) =>
      topic.title.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredTopics(filtered);
    if (query.trim() === "") {
      setFilteredTopics(helpTopics);
    }
  };

  const handleTopicClick = (topic: typeof helpTopics[0]) => {
    if (onStartConversationWithMessage && topic.startingMessage) {
      onStartConversationWithMessage(topic.startingMessage);
    }
  };

  const handleBackToTopics = () => {
    setSelectedTopic(null);
    if (hideTabsWhenDetail) {
      hideTabsWhenDetail(false);
    }
  };

  const handleNewConversation = () => {
    if (onStartNewConversation) {
      onStartNewConversation();
    }
  };

  if (selectedTopic) {
    return <TopicDetailView topic={selectedTopic} onBack={handleBackToTopics} />;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 pb-32">
        <div className="max-w-2xl mx-auto">
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-1">
              {!userName || userName === "User"
                ? t('chatbot.helloGeneral')
                : t('chatbot.hello', { userName })}
            </h2>
            <p className="text-sm text-muted-foreground">{t('chatbot.howCanWeHelp')}</p>
          </div>

          <form onSubmit={handleSearch} className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
              <Input
                type="text"
                placeholder={t('chatbot.searchPlaceholder')}
                value={searchQuery}
                onChange={handleSearchInputChange}
                className="pl-9 text-sm h-9"
              />
            </div>
          </form>

          <div className="space-y-3">
            <h3 className="text-sm font-medium text-muted-foreground">{t('chatbot.popularTopics')}</h3>
            <div className="grid gap-3">
              {filteredTopics.map((topic) => (
                <div
                  key={topic.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 hover:border-gray-300 hover:shadow-sm`}
                  onClick={() => handleTopicClick(topic)}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-lg">{topic.icon}</span>
                    <div>
                      <h4 className="text-sm font-medium">{topic.title}</h4>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {filteredTopics.length === 0 && (
            <div className="text-center py-6">
              <p className="text-sm text-muted-foreground">{t('chatbot.noTopicsFound')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Sticky New Conversation Button */}
      <div className="absolute bottom-16 left-0 right-0 p-4 bg-gradient-to-t from-white via-white to-transparent">
        <div className="max-w-2xl mx-auto">
          <Button 
            onClick={handleNewConversation}
            className="w-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange text-white hover:from-pink-600 hover:to-orange-600 transition-all duration-200 py-5 relative shadow-lg border-0 font-semibold"
            size="lg"
            style={{
              background: 'linear-gradient(to right, #FF1493, #FFA500)',
            }}
          >
            <div className="absolute left-4 bg-white rounded-full p-1">
              <Plus className="w-4 h-4 text-gebeya-pink" />
            </div>
            <span className="text-base font-semibold">{t('chatbot.newConversation')}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HomeTab; 